import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Parcel } from "@/mocks/parcels";

interface ParcelModalProps {
  mode: "add" | "edit";
  parcel?: Parcel | null;
  onSave: (parcel: Parcel) => void;
  onClose: () => void;
  saving?: boolean;
  saveError?: string | null;
}

const STATUS_OPTIONS = ["Processing", "In Transit", "Out for Delivery", "Delivered", "Exception"];

const emptyParcel: Parcel = {
  id: "",
  status: "Processing",
  sender: "",
  recipient: "",
  phone: "",
  deliveryAddress: "",
  weight: "",
  dimensions: "",
  origin: "",
  destination: "",
  currentLocation: "",
  lat: 0,
  lng: 0,
  estimatedDelivery: "",
  lastUpdated: new Date().toISOString().slice(0, 16).replace("T", " "),
  timeline: [],
};

export default function ParcelModal({ mode, parcel, onSave, onClose, saving = false, saveError = null }: ParcelModalProps) {
  const { t } = useTranslation();
  const [form, setForm] = useState<Parcel>(parcel || emptyParcel);
  const [mapPreviewQuery, setMapPreviewQuery] = useState(
    parcel?.currentLocation || ""
  );
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setForm(parcel || emptyParcel);
    setMapPreviewQuery(parcel?.currentLocation || "");
  }, [parcel]);

  const handleChange = (field: keyof Parcel, value: string | number) => {
    setForm((prev) => ({ ...prev, [field]: value }));

    // Debounce map preview update when location changes
    if (field === "currentLocation" && typeof value === "string") {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        setMapPreviewQuery(value);
      }, 800);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const now = new Date().toISOString().slice(0, 16).replace("T", " ");
    onSave({ ...form, lastUpdated: now });
  };

  const mapSrc = mapPreviewQuery
    ? `https://maps.google.com/maps?q=${encodeURIComponent(mapPreviewQuery)}&z=12&output=embed&hl=en`
    : null;

  const inputClass = "w-full h-10 px-3 rounded-lg border border-gray-200 text-sm outline-none focus:border-emerald-400 transition-colors";
  const labelClass = "block text-xs font-medium text-slate-600 mb-1";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white rounded-t-2xl">
          <h2 className="font-bold text-slate-800 text-base">
            {mode === "add" ? t("admin_modal_add_title") : t("admin_modal_edit_title")}
          </h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 text-slate-500 cursor-pointer"
          >
            <i className="ri-close-line" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>{t("admin_field_tracking")} *</label>
              <input
                required
                value={form.id}
                onChange={(e) => {
                  const raw = e.target.value.toUpperCase();
                  handleChange("id", raw);
                }}
                onBlur={(e) => {
                  // Auto-format: if user typed just digits, prepend SE-
                  const val = e.target.value.trim().toUpperCase();
                  if (/^\d{9}$/.test(val)) {
                    handleChange("id", `SE-${val}`);
                  }
                }}
                placeholder="SE-000000000"
                pattern="SE-\d{9}"
                title="Format must be SE- followed by exactly 9 digits (e.g. SE-123456789)"
                className={`${inputClass} ${mode === "edit" ? "bg-gray-50 text-gray-500" : ""}`}
                readOnly={mode === "edit"}
              />
              {mode === "add" && form.id && !/^SE-\d{9}$/.test(form.id) && (
                <p className="text-xs text-amber-500 mt-1">
                  Must be <span className="font-mono font-semibold">SE-</span> followed by exactly 9 digits
                </p>
              )}
            </div>

            <div>
              <label className={labelClass}>{t("admin_field_status")} *</label>
              <select
                required
                value={form.status}
                onChange={(e) => handleChange("status", e.target.value)}
                className={inputClass}
              >
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>

            <div>
              <label className={labelClass}>{t("admin_field_sender")}</label>
              <input value={form.sender} onChange={(e) => handleChange("sender", e.target.value)} className={inputClass} />
            </div>

            <div>
              <label className={labelClass}>{t("admin_field_recipient")}</label>
              <input value={form.recipient} onChange={(e) => handleChange("recipient", e.target.value)} className={inputClass} />
            </div>

            <div>
              <label className={labelClass}>{t("admin_field_phone")}</label>
              <input
                value={form.phone}
                onChange={(e) => handleChange("phone", e.target.value)}
                placeholder="e.g. +1 (212) 555-0147"
                className={inputClass}
              />
            </div>

            <div className="sm:col-span-2">
              <label className={labelClass}>{t("admin_field_delivery_address")}</label>
              <input
                value={form.deliveryAddress}
                onChange={(e) => handleChange("deliveryAddress", e.target.value)}
                placeholder="e.g. 245 Park Avenue, New York, NY 10167, USA"
                className={inputClass}
              />
            </div>

            <div>
              <label className={labelClass}>{t("admin_field_weight")}</label>
              <input value={form.weight} onChange={(e) => handleChange("weight", e.target.value)} placeholder="e.g. 2.5 kg" className={inputClass} />
            </div>

            <div>
              <label className={labelClass}>{t("admin_field_dimensions")}</label>
              <input value={form.dimensions} onChange={(e) => handleChange("dimensions", e.target.value)} placeholder="e.g. 30x20x15 cm" className={inputClass} />
            </div>

            <div>
              <label className={labelClass}>{t("admin_field_origin")}</label>
              <input value={form.origin} onChange={(e) => handleChange("origin", e.target.value)} className={inputClass} />
            </div>

            <div>
              <label className={labelClass}>{t("admin_field_destination")}</label>
              <input value={form.destination} onChange={(e) => handleChange("destination", e.target.value)} className={inputClass} />
            </div>

            <div className="sm:col-span-2">
              <label className={labelClass}>
                {t("admin_field_location")}
                <span className="ml-1 text-slate-400 font-normal">(this is what shows on the map)</span>
              </label>
              <input
                value={form.currentLocation}
                onChange={(e) => handleChange("currentLocation", e.target.value)}
                placeholder="e.g. Lagos, Nigeria"
                className={inputClass}
              />
            </div>

            {/* Live Map Preview */}
            <div className="sm:col-span-2">
              <label className={labelClass}>Map Preview</label>
              <div className="w-full h-48 rounded-lg overflow-hidden border border-gray-200 bg-slate-50">
                {mapSrc ? (
                  <iframe
                    key={mapSrc}
                    title="Map Preview"
                    src={mapSrc}
                    className="w-full h-full border-0"
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center gap-2 text-slate-400">
                    <div className="w-8 h-8 flex items-center justify-center">
                      <i className="ri-map-pin-line text-2xl" />
                    </div>
                    <p className="text-xs">Enter a location above to preview the map</p>
                  </div>
                )}
              </div>
              <p className="text-xs text-slate-400 mt-1">
                The map updates automatically as you type the location. This is exactly what users will see.
              </p>
            </div>

            <div className="sm:col-span-2">
              <label className={labelClass}>{t("admin_field_estimated")}</label>
              <input
                type="date"
                value={form.estimatedDelivery}
                onChange={(e) => handleChange("estimatedDelivery", e.target.value)}
                className={inputClass}
              />
            </div>
          </div>

          {/* Footer */}
          <div className="flex flex-col gap-3 mt-6 pt-4 border-t border-gray-100">
            {saveError && (
              <div className="flex items-center gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-red-600 text-xs">
                <div className="w-4 h-4 flex items-center justify-center shrink-0">
                  <i className="ri-error-warning-line" />
                </div>
                {saveError}
              </div>
            )}
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                disabled={saving}
                className="px-5 py-2 rounded-lg border border-gray-200 text-slate-600 text-sm font-medium hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
              >
                {t("admin_cancel")}
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-5 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 flex items-center gap-2"
              >
                {saving && (
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-loader-4-line animate-spin" />
                  </div>
                )}
                {saving ? "Saving..." : t("admin_save")}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
